import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTenant } from "../../../Contexts/TenantContext";
import { useAuth } from "../../../Contexts/AuthContext";
import { toPublicPath } from "../../../Utils/publicRoutes";
import { UserLogin } from "../../../Models/User";
import { Input } from "../../../Components/Input/Input";
import { FaLock, FaSignInAlt, FaUser } from "react-icons/fa";
import { GoogleIcon } from "../../../Components/GoogleIcon/GoogleIcon";
import FooterPolicies from "../../../Components/FooterPolicies/FooterPolicies";
import ForgotPasswordFlow from "../../../Components/ForgotPasswordFlow/ForgotPasswordFlow";
import LoadingOverlay from "../../../Components/LoadingOverlay/LoadingOverlay";
import Button from "../../../Components/Button/Button";

const UserSignInPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useTenant();
  const { login, googlePopupLogin } = useAuth();

  const handleGoogleSignIn = () => {
    void googlePopupLogin('signin').then((authenticatedUser) => {
      if (authenticatedUser?.requiresContactInfoUpdate) {
        navigate(toPublicPath("/completar-cadastro"));
      } else if (authenticatedUser) {
        navigate("/");
      }
    });
  };

  const [isForgotPasswordFlowOpen, setIsForgotPasswordFlowOpen] = useState(false);
  const blurClass = isForgotPasswordFlowOpen ? "blur-sm pointer-events-none select-none" : "";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userData, setUserData] = useState<UserLogin>({
    email: "",
    password: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setUserData(prevUser => ({ ...prevUser, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const loggedInUser = await login(userData);
    setIsSubmitting(false);

    if (!loggedInUser) {
      return;
    }

    if (loggedInUser.requiresContactInfoUpdate) {
      navigate(toPublicPath("/completar-cadastro"));
      return;
    }

    if (location.state?.from === "checkout") {
      navigate(toPublicPath("/carrinho"));
    } else {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      {isSubmitting && <LoadingOverlay fullscreen />}
      <div className={`bg-white flex flex-col flex-grow items-center px-4 py-10 ${blurClass}`}>
        <Link to="/" className="mb-8">
          <img
            src={tenant?.logo?.url}
            alt="Logo"
            className="w-44 mx-auto mb-8 sm:w-48"
          />
        </Link>

        <div className="w-full flex flex-grow items-center justify-center">
          <div className="w-full bg-white max-w-xs">
            <h2 className="text-xl md:text-2xl text-primary-600 font-semibold text-center mb-3 md:mb-6">
              {"ACESSE SUA CONTA"}
            </h2>

            <div className="mb-4 md:mb-6 space-y-3">
              <button
                type="button"
                className="w-full border border-gray-300 bg-white text-gray-800 font-semibold py-3 rounded flex justify-center items-center gap-2 transition-colors hover:border-primary-600"
                onClick={handleGoogleSignIn}
              >
                <GoogleIcon size={20} />
                {"ENTRAR COM GOOGLE"}
              </button>

              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span className="h-px flex-1 bg-gray-300" />
                <span>{"ou"}</span>
                <span className="h-px flex-1 bg-gray-300" />
              </div>
            </div>

            <form className="space-y-3 md:space-y-6" onSubmit={handleSubmit}>
              <Input
                label={"E-mail"}
                name="email"
                type="email"
                placeholder="seu@email.com"
                value={userData.email}
                onChange={handleChange}
                required
                leftIcon={<FaUser />}
              />

              <Input
                label={"Senha"}
                name="password"
                type="password"
                placeholder="Sua senha"
                value={userData.password}
                onChange={handleChange}
                required
                leftIcon={<FaLock />}
              />

              <Button isLoading={isSubmitting} icon={<FaSignInAlt />}
                type="submit"
                className="w-full py-3 flex justify-center items-center gap-2 mb-2"
                disabled={isSubmitting}
              >
                {"ENTRAR"}
              </Button>

              <div className="text-right md:mb-4">
                <button
                  type="button"
                  onClick={() => setIsForgotPasswordFlowOpen(true)}
                  className="text-sm text-gray-500 underline hover:text-primary-600"
                >
                  {"Esqueceu a senha?"}
                </button>
              </div>
            </form>

            <div className="w-full flex justify-center items-center text-sm gap-1 py-8 md:py-10">
              <p>{"Não possui conta?"}</p>
              <Link
                to={toPublicPath("/cadastro")}
                className="text-primary-600 underline font-bold hover:text-primary-700"
              >
                {"CADASTRAR-SE"}
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className={blurClass}>
        <FooterPolicies />
      </div>

      <ForgotPasswordFlow
        isOpen={isForgotPasswordFlowOpen}
        onClose={() => setIsForgotPasswordFlowOpen(false)}
      />
    </div>
  );
};

export default UserSignInPage;
